"use client";

import { useEffect, useRef } from "react";
import { trackScrollDepth, trackCTAClick, trackEmailClick, trackOutboundClick } from "src/lib/analytics";

export function AnalyticsTracker() {
  const firedDepths = useRef(new Set<number>());

  useEffect(() => {
    // StackAdapt first-party retargeting beacon (ad-blocker-proof — same-origin)
    try {
      const params = new URLSearchParams({
        ref: document.referrer,
        title: document.title,
        utm_source: new URLSearchParams(window.location.search).get("utm_source") ?? "",
      });
      fetch(`/_sa/rt?${params}`, { method: "GET", credentials: "include", keepalive: true }).catch(() => {});
    } catch {}

    // Scroll depth tracking (25%, 50%, 75%, 100%)
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const percent = Math.round((scrollTop / docHeight) * 100);

      for (const threshold of [25, 50, 75, 100]) {
        if (percent >= threshold && !firedDepths.current.has(threshold)) {
          firedDepths.current.add(threshold);
          trackScrollDepth(threshold);
        }
      }
    };

    // Click tracking for CTAs, mailto, and outbound links
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const text = anchor.textContent?.trim() || "";

      if (href.startsWith("mailto:")) {
        trackEmailClick(href.replace("mailto:", ""));
      } else if (href.startsWith("http") && !href.includes("piercebrothersmulholland.com")) {
        trackOutboundClick(href, text);
      } else if (anchor.closest("button, [role='button']") || anchor.classList.contains("bg-turquoise") || anchor.classList.contains("bg-teal")) {
        trackCTAClick(text, href);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("click", handleClick);

    // Reset on navigation
    const resetDepths = () => { firedDepths.current.clear(); };
    window.addEventListener("popstate", resetDepths);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClick);
      window.removeEventListener("popstate", resetDepths);
    };
  }, []);

  return null;
}
